import pluralize from 'pluralize';

import { useCandidateRejectionMessagesQuery } from '@/modules/candidates/queries/useCandidateRejectionMessagesQuery';
import { Skeleton } from '@/modules/ui/components/skeleton/Skeleton';

interface RejectionMessagePreviewProps {
  jobId: string;
  candidates: Array<{ id: string; fullName: string }>;
}

/**
 * Read-only preview of the default rejection message a bulk rejection sends.
 * Renders nothing when no selected candidate finished pre-screening.
 */
export function RejectionMessagePreview({
  jobId,
  candidates,
}: RejectionMessagePreviewProps) {
  const recipientsQuery = useCandidateRejectionMessagesQuery({
    jobId,
    candidateIds: candidates.map((candidate) => candidate.id),
  });

  if (recipientsQuery.isPending) {
    return <Skeleton className="h-24 w-full" />;
  }

  if (recipientsQuery.isError) {
    return (
      <p className="text-muted-foreground text-xs">
        The rejection message preview could not be loaded. Candidates who
        finished pre-screening will still be sent the default message.
      </p>
    );
  }

  const recipients = recipientsQuery.data;
  const example = recipients[0];
  if (!example) {
    return null;
  }

  const exampleName = candidates.find(
    (candidate) => candidate.id === example.candidateId,
  )?.fullName;

  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm font-medium text-neutral-800">
        {recipients.length} of {candidates.length} selected{' '}
        {pluralize('candidate', candidates.length)} will receive this message on
        LinkedIn
      </p>
      <p className="rounded-md border bg-neutral-50 px-3 py-2 text-sm whitespace-pre-wrap text-neutral-700">
        {example.content}
      </p>
      <p className="text-muted-foreground text-xs">
        {exampleName ? `Shown as sent to ${exampleName}; ` : ''}each candidate
        is greeted by their first name. The message cannot be edited when
        rejecting in bulk — reject a candidate on their own to edit it.
      </p>
    </div>
  );
}
